import {useLocalStorage} from 'platform/components';

import {useCallback, useEffect, useReducer, useRef, useState} from 'react';

import {isEmpty, isNotNil} from 'ramda';

import {Nullish, useQueryState} from 'shared/web';

import {AUCTION_DEFAULT_ORDER_ID} from '../../../constants/AUCTION_DEFAULT_ORDER_ID';
import {CLASSIFIEDS_DEFAULT_ORDER_ID} from '../../../constants/CLASSIFIEDS_DEFAULT_ORDER_ID';
import {sourcingStorageKey} from '../../../constants/sourcingStorageKey';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {useFilters} from '../../../pages/AllOffers/components/FiltersContext/FiltersContext';
import {reducer, SourcingTableDispatch} from '../../../store/sourcingTable/reducer';
import {SourcingTableName} from '../types/SourcingTableName';
import {SourcingTableState} from '../types/SourcingTableState';
import {sourcingTableSessionStorage} from '../utils/sourcingTableSessionStorage';

export function useSourcingTable(
  tableName: SourcingTableName,
  initialOrderBy: string,
  appliedFilterPresetId?: string | Nullish
): [SourcingTableState, SourcingTableDispatch] {
  const [queryFilterPresetId] = useQueryState('filterPresetId');
  const [orderByFromLocalStorage, saveOrderByToLocalStorage] = useLocalStorage(
    `${sourcingStorageKey.TABLE_SORTING_PREFIX}${tableName}`,
    initialOrderBy
  );
  const [isRealTimeFromLocalStorage, saveIsRealTimeToLocalStorage] = useLocalStorage(
    `${sourcingStorageKey.TABLE_REAL_TIME_PREFIX}${tableName}`,
    false
  );
  const sourcingType = useSourcingType();

  const [state, dispatch] = useReducer(reducer, {
    tableName,
    orderBy: isEmpty(orderByFromLocalStorage) ? initialOrderBy : orderByFromLocalStorage,
    page: 1,
    checkedRows: [],
    selectedRow: null,
    expandedRowIds: [],
    isRealTime: isRealTimeFromLocalStorage,
  });

  const {filters} = useFilters();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();

  // Render-time page reset: dispatch during render so consumers reading state.page see 1 in the
  // same render as the input change, avoiding the one-render race that a useEffect would create.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevQueryInputs, setPrevQueryInputs] = useState({
    filters,
    currencyCode: currency?.code,
    countryCode: targetCountry?.code,
    pricingStrategy,
    appliedFilterPresetId,
  });
  const queryInputsChanged =
    prevQueryInputs.filters !== filters ||
    prevQueryInputs.currencyCode !== currency?.code ||
    prevQueryInputs.countryCode !== targetCountry?.code ||
    prevQueryInputs.pricingStrategy !== pricingStrategy ||
    prevQueryInputs.appliedFilterPresetId !== appliedFilterPresetId;

  if (queryInputsChanged) {
    setPrevQueryInputs({
      filters,
      currencyCode: currency?.code,
      countryCode: targetCountry?.code,
      pricingStrategy,
      appliedFilterPresetId,
    });
    if (state.page !== 1) {
      dispatch({type: 'changePage', page: 1});
    }
  }

  // Persist preferences in the dispatch path instead of syncing state → storage via useEffect.
  const dispatchWithPersistence: SourcingTableDispatch = useCallback(
    (action) => {
      if (action.type === 'sort') {
        saveOrderByToLocalStorage(action.column);
        if (!action.keepRealTime) {
          saveIsRealTimeToLocalStorage(false);
        }
      } else if (action.type === 'setRealTime') {
        saveIsRealTimeToLocalStorage(action.isRealTime);
        if (action.isRealTime && action.orderBy) {
          saveOrderByToLocalStorage(action.orderBy);
        }
      }
      dispatch(action);
    },
    [saveOrderByToLocalStorage, saveIsRealTimeToLocalStorage]
  );

  // Query parameter is added when clicking on notification; handled in useSourcingFilterPresets()
  useEffect(
    function resetSortOnNotificationPreset() {
      if (isNotNil(queryFilterPresetId)) {
        dispatchWithPersistence({
          type: 'sort',
          column:
            sourcingType === SourcingType.AUCTIONS
              ? AUCTION_DEFAULT_ORDER_ID
              : CLASSIFIEDS_DEFAULT_ORDER_ID,
        });
      }
    },
    [queryFilterPresetId, sourcingType, dispatchWithPersistence]
  );

  // Skip the first run so the initial `state.page === 1` does not overwrite the page that
  // `useSourcingTableSelectionEffects` is about to restore from session storage on first data
  // arrival.
  const hasMountedRef = useRef(false);
  useEffect(
    function persistLastVisitedPage() {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        return;
      }
      sourcingTableSessionStorage.setLastVisitedPage(tableName, state.page);
    },
    [state.page, tableName]
  );

  return [state, dispatchWithPersistence];
}
